import { useState } from "react";
import { toast } from "react-toastify";
import { FiCheck, FiCornerUpLeft, FiX, FiEdit2 } from "react-icons/fi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, DataTable, PersonCell, Button, Tabs, Modal, FormField, ErrorState, Banner } from "../../components/common";
import ProposalFormModal from "../../components/proposals/ProposalFormModal";
import { listProposals, getProposal, decideProposal } from "../../services/kraKpiProposalApi";
import { getErrorMessage, getErrorCode } from "../../utils/errors";
import { formatDate, formatDateTime } from "../../utils/format";

const DECISIONS = {
  APPROVE: { title: "Approve", tone: "primary", hint: "Optional note for the employee.", done: "Approved. It is now on their KRA & KPI sheet (or in the library)." },
  RETURN: { title: "Return for changes", tone: "primary", hint: "Required: what should they change?", done: "Returned to the employee for changes." },
  REJECT: { title: "Reject", tone: "danger", hint: "Required: why is it rejected?", done: "Rejected." },
};

export default function KraKpiProposalsPage() {
  const [tab, setTab] = useState("pending");
  const list = useAsync(() => listProposals({ view: "review", status: tab === "pending" ? "PENDING_APPROVAL" : undefined }), [tab]);
  const [deciding, setDeciding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const decide = async () => {
    setBusy(true);
    try {
      await decideProposal(deciding.proposal, deciding.decision, deciding.comment);
      toast.success(DECISIONS[deciding.decision].done);
      setDeciding(null);
      list.reload();
    } catch (err) {
      const onSheet = deciding.decision === "APPROVE" && deciding.proposal.recordId;
      toast.error(onSheet && getErrorCode(err) === "INVALID_STATE"
        ? `${deciding.proposal.employeeName}'s goal sheet is no longer in goal setting (already submitted or approved), so this item can't be added to it. Return their goal sheet from Goal approvals first, then approve this proposal.`
        : getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (p) => {
    try { setDetail((await getProposal(p.id)).data); } catch (err) { toast.error(getErrorMessage(err)); }
  };

  if (list.error) return <ErrorState message={list.error} onRetry={list.reload} />;
  const rows = tab === "pending" ? list.data || [] : (list.data || []).filter((p) => p.status !== "PENDING_APPROVAL");

  return (
    <div className="page">
      <PageHeader title="KRA & KPI approvals" subtitle="KRAs and KPIs your team members added. Approve, edit, return for changes or reject them." />
      <Tabs tabs={[{ key: "pending", label: "Waiting for you", count: tab === "pending" ? rows.length : undefined }, { key: "decided", label: "History" }]} value={tab} onChange={setTab} />
      <Card padded={false}>
        <DataTable
          label="KRA and KPI proposals"
          rows={rows}
          loading={list.loading && !list.data}
          emptyTitle={tab === "pending" ? "Nothing waiting for you" : "No decided proposals yet"}
          onRowClick={openDetail}
          columns={[
            { key: "employee", label: "Employee", render: (p) => <PersonCell name={p.employeeName} meta={p.employeeId} /> },
            {
              key: "proposal", label: "Proposal",
              render: (p) => (
                <span className="cell-stack">
                  <strong>{p.type}: {p.title}</strong>
                  <small>{p.type === "KPI" && (p.parentProposalTitle || p.kraName) ? `Under ${p.parentProposalTitle || p.kraName}. ` : ""}{p.description}</small>
                </span>
              ),
            },
            { key: "plan", label: "On the sheet", render: (p) => (p.recordId ? `${p.weightage}% · target ${p.target} ${p.unit} · due ${formatDate(p.dueDate)}` : "Library only") },
            { key: "status", label: "Status", render: (p) => <span className={`badge badge-${p.tone}`}><span className="badge-dot" />{p.statusLabel}</span> },
            {
              key: "act", label: "", align: "right",
              render: (p) => p.status === "PENDING_APPROVAL" && (
                <span className="row-actions" onClick={(e) => e.stopPropagation()} role="presentation">
                  <Button size="sm" variant="ghost" icon={FiEdit2} aria-label={`Edit ${p.title}`} onClick={() => setEditing(p)} />
                  <Button size="sm" icon={FiCheck} onClick={() => setDeciding({ proposal: p, decision: "APPROVE", comment: "" })}>Approve</Button>
                  <Button size="sm" variant="secondary" icon={FiCornerUpLeft} onClick={() => setDeciding({ proposal: p, decision: "RETURN", comment: "" })}>Return</Button>
                  <Button size="sm" variant="ghost" icon={FiX} onClick={() => setDeciding({ proposal: p, decision: "REJECT", comment: "" })}>Reject</Button>
                </span>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={Boolean(deciding)}
        size="sm"
        title={`${deciding ? DECISIONS[deciding.decision].title : ""}: ${deciding?.proposal.title || ""}`}
        subtitle={deciding?.proposal.employeeName}
        onClose={busy ? undefined : () => setDeciding(null)}
        footer={<><Button variant="ghost" onClick={() => setDeciding(null)} disabled={busy}>Cancel</Button>
          <Button variant={deciding?.decision === "REJECT" ? "danger" : "primary"} onClick={decide} loading={busy}>{deciding ? DECISIONS[deciding.decision].title : ""}</Button></>}
      >
        {deciding && (
          <div className="form-stack">
            {deciding.decision === "APPROVE" && deciding.proposal.recordId && (
              <Banner tone="info">It is added to {deciding.proposal.employeeName}&apos;s KRA &amp; KPI sheet now, so goal setting must still be open for them.</Banner>
            )}
            <FormField label="Comment for the employee" hint={DECISIONS[deciding.decision].hint}>
              <textarea rows={3} value={deciding.comment} onChange={(e) => setDeciding((d) => ({ ...d, comment: e.target.value }))} />
            </FormField>
          </div>
        )}
      </Modal>

      <ProposalFormModal open={Boolean(editing)} mode="review" proposal={editing} onClose={() => setEditing(null)} onSaved={list.reload} />

      <Modal open={Boolean(detail)} title={detail?.title} subtitle={`${detail?.type} proposed by ${detail?.employeeName}`} onClose={() => setDetail(null)}>
        {detail && (
          <div className="form-stack">
            <p>{detail.description || <span className="muted">No description.</span>}</p>
            <p className="form-hint">Measured in {detail.unit}{detail.recordId ? `; ${detail.weightage}% weight, target ${detail.target}, due ${formatDate(detail.dueDate)}` : "; library only"}.</p>
            <p className="detail-label">History</p>
            <ul className="icon-list">
              {detail.history.map((h, i) => (
                <li key={i}>{formatDateTime(h.at)} — {h.event.replace("KRA_KPI_", "").replace(/_/g, " ").toLowerCase()} by {h.by}{h.reason ? `: ${h.reason}` : ""}</li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
}
